/**
 * A step's result on its card. Closed, it lists only the fields other steps take; opened, every
 * field as a tree (or a search), each with a port to drag onto an argument.
 */

import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CustomToolStep } from '@/types/custom-tool.types';
import { useCustomToolBuilder } from '../../builder/builder-context';
import { usedOutputs } from '../flow-layout';
import { outPort } from '../ports';
import { ancestorsOfAll, searchRows, treeRows, type OutputRow } from '../output-tree';
import { INTERACTIVE, Row } from './card';
import { OutputPath } from './output-path';
import { Port } from './port';
import { usePortsChanged } from './use-ports-changed';

const SEARCH_FROM = 8;
const INDENT = ['pl-3', 'pl-6', 'pl-9', 'pl-12', 'pl-15', 'pl-18'];

interface FieldRowProps {
	row: OutputRow;
	used: boolean;
	/** The tree shows a field's own name under its parent; a list or search shows the whole path. */
	flat: boolean;
	open: boolean;
	/** Held open because a field under it is wired, so its wire keeps a row to start from. */
	locked: boolean;
	onToggle: (path: string) => void;
}

const FieldRow = ({ row, used, flat, open, locked, onToggle }: FieldRowProps) => (
	<Row className={INDENT[Math.min(row.depth, INDENT.length - 1)]}>
		{row.hasChildren ? (
			<button
				type='button'
				aria-label={locked ? `${row.path} holds a wired field` : `${open ? 'Close' : 'Open'} ${row.path}`}
				aria-expanded={open}
				disabled={locked}
				title={locked ? 'Holds a wired field' : undefined}
				onClick={() => onToggle(row.path)}
				className={cn(INTERACTIVE, '-ml-1 shrink-0 rounded p-0.5 text-gray-800 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent')}
			>
				<ChevronRight className={cn('size-3 transition-transform', open && 'rotate-90')} />
			</button>
		) : (
			!flat && <span className='w-3 shrink-0' />
		)}
		{flat ? (
			<OutputPath path={row.path} />
		) : (
			<span title={row.path} className={cn('min-w-0 flex-1 truncate font-mono text-[11px] leading-4', used ? 'text-violet-800' : 'text-gray-900')}>
				{row.label === '0' ? 'first item' : row.label}
			</span>
		)}
		<Port id={outPort(row.path)} type='source' side='right' tone={used ? 'step' : 'step-free'} />
	</Row>
);

export const StepOutputs = ({ step, paths }: { step: CustomToolStep; paths: string[] }) => {
	const { draft } = useCustomToolBuilder();
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
	const used = usedOutputs(draft.steps, step.id);
	// A field the tool's result no longer lists still carries its wire.
	const all = [...paths, ...used.filter((path) => !paths.includes(path))];
	const searching = open && query.trim() !== '';
	const forcedOpen = ancestorsOfAll(used);
	const openPaths = new Set([...expanded, ...forcedOpen]);
	const rows = !open ? searchRows(used, '', used) : searching ? searchRows(all, query, used) : treeRows(all, openPaths);
	usePortsChanged(rows.map((row) => row.path).join(','));

	const toggle = (path: string) =>
		setExpanded((current) => {
			const next = new Set(current);
			if (next.has(path)) next.delete(path);
			else next.add(path);
			return next;
		});

	if (!all.length) return null;
	return (
		<div className='border-t border-gray-300 pb-1'>
			<button
				type='button'
				aria-expanded={open}
				onClick={() => setOpen((current) => !current)}
				className={cn(INTERACTIVE, 'flex h-7 w-full items-center gap-1 px-3 text-[11px] leading-4 text-gray-800 hover:text-ink')}
			>
				<ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} aria-hidden />
				<span>Outputs</span>
				<span className='text-gray-700 tabular-nums'>{paths.length}</span>
				{!open && <span className='ml-auto truncate text-gray-700'>{used.length ? `${used.length} passed on` : 'Open to wire a field'}</span>}
			</button>
			{open && all.length >= SEARCH_FROM && (
				<div className='px-3 pb-1'>
					<input
						type='search'
						aria-label={`Find a field of step ${draft.steps.indexOf(step) + 1}`}
						placeholder='Find a field'
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						className={cn(INTERACTIVE, 'h-6 w-full rounded-md border border-gray-500 bg-white px-2 text-[11px] leading-4 text-ink outline-none focus:border-indigo-500')}
					/>
				</div>
			)}
			{rows.map((row) => (
				<FieldRow
					key={row.path}
					row={row}
					used={used.includes(row.path)}
					flat={!open || searching}
					open={openPaths.has(row.path)}
					locked={forcedOpen.includes(row.path)}
					onToggle={toggle}
				/>
			))}
			{searching && rows.length === 0 && (
				<Row>
					<span className='text-[11px] leading-4 text-gray-800'>No field matches</span>
				</Row>
			)}
		</div>
	);
};
